import React, { useState, useRef } from 'react';
import './FileUploader.css';
import { file } from 'jszip';

const FileUploader = ({ onFilesSelected, onProcess, onStlUpload, isLoading }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileType, setFileType] = useState('nii');
  const [threshold, setThreshold] = useState(0.5);
  const [dragOver, setDragOver] = useState(false);
  const [folderName, setFolderName] = useState('');
  
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const stlInputRef = useRef(null);

  const handleFileSelect = (event, isDicom = false, isStl = false) => {
    const files = Array.from(event.target.files);
    
    if (isStl) {
      // Для STL принимаем несколько файлов
      const stlFiles = files.filter(file => file.name.toLowerCase().endsWith('.stl'));
      if (stlFiles.length > 0) {
        setSelectedFiles(stlFiles);
        setFileType('stl');
        setFolderName('');
        // Передаем все STL файлы
        stlFiles.forEach((file, index) => {
          setTimeout(() => {
            onStlUpload(file);
          }, index * 100);
        });
      } else {
        alert('Пожалуйста, выберите STL файлы');
      }
      return;
    }

    if (isDicom) {
      // Для DICOM создаем ZIP архив из файлов
      createZipFromDicomFiles(files);
    } else {
      // Для NIfTI выбираем отдельные файлы
      const validFiles = files.filter(file => 
        file.name.toLowerCase().endsWith('.nii') || 
        file.name.toLowerCase().endsWith('.nii.gz')
      );

      if (validFiles.length > 0) {
        setSelectedFiles(validFiles);
        setFileType('nii');
        setFolderName('');
        onFilesSelected(validFiles, 'nii');
      } else {
        alert('Пожалуйста, выберите файлы правильного формата');
      }
    }
  };

  // Функция для создания ZIP архива из DICOM файлов
  const createZipFromDicomFiles = async (files) => {
    try {
      // Фильтруем DICOM файлы (теперь принимаем все файлы из папки)
      // Так как файлы могут не иметь расширения .dcm
      if (files.length === 0) {
        alert('Папка пуста');
        return;
      }
      
      
      
    let minSize = Infinity;
    let minIndex = -1;
    
    files.forEach((file, index) => {
      if (file.size < minSize) {
        minSize = file.size;
        minIndex = index;
      }
    })
    
    if (minIndex !== -1) {
      const smallestFile = files[minIndex];
      console.log(`Удаляем самый маленький файл: ${smallestFile.name} (${smallestFile.size} bytes)`);
      
      // Создаем новый массив без самого маленького файла
       files = files.filter((_, index) => index !== minIndex);
    }
     
      console.log(`Найдено файлов в папке: ${files.length}`);
      
      // Импортируем JSZip
      const JSZip = await import('jszip');
      const zip = new JSZip.default();
      
      // Добавляем все файлы в ZIP (без фильтрации по расширению)
      files.forEach((file, index) => {
        const fileName = file.webkitRelativePath || file.name;
        console.log(`Добавление файла ${index + 1}: ${fileName}`);
        zip.file(fileName, file);
      });

      // Создаем ZIP файл
      console.log('Создание ZIP архива...');
      const zipBlob = await zip.generateAsync({ 
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: {
          level: 6
        }
      });
      
      const zipFile = new File([zipBlob], 'dicom_files.zip', { type: 'application/zip' });
      
      console.log(`ZIP архив создан: ${zipFile.size} bytes`);
      setSelectedFiles([zipFile]);
      setFileType('dicom_zip');
      setFolderName(files[0].webkitRelativePath.split('/')[0] || 'DICOM папка');
      
      // Передаем ZIP файл
      onFilesSelected([zipFile], 'dicom_zip');
      
    } catch (error) {
      console.error('Ошибка создания ZIP архива:', error);
      alert('Ошибка при создании ZIP архива: ' + error.message);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e, isDicom = false, isStl = false) => {
    e.preventDefault();
    setDragOver(false);
    
    // Для drag & drop получаем файлы через dataTransfer
    const items = e.dataTransfer.items;
    const files = [];
    
    // Обрабатываем файлы из drag & drop
    for (let i = 0; i < items.length; i++) {
      const item = items[i].webkitGetAsEntry();
      if (item) {
        traverseFileTree(item, files);
      }
    }
    
    // Ждем немного, чтобы файлы успели загрузиться
    setTimeout(() => {
      if (isStl) {
        // Обработка STL файлов
        // (здесь нужно реализовать получение файлов из drag & drop)
        alert('Перетаскивание STL файлов временно не поддерживается. Используйте кнопку выбора файлов.');
      } else if (isDicom) {
        // Для DICOM создаем ZIP из перетащенных файлов
        if (files.length > 0) {
          createZipFromDicomFiles(files);
        }
      } else {
        // Обработка NIfTI файлов
        const validFiles = files.filter(file => 
          file.name.toLowerCase().endsWith('.nii') || 
          file.name.toLowerCase().endsWith('.nii.gz')
        );
        
        if (validFiles.length > 0) {
          setSelectedFiles(validFiles);
          setFileType('nii');
          setFolderName('');
          onFilesSelected(validFiles, 'nii');
        }
      }
    }, 100);
  };

  // Рекурсивная функция для обхода дерева файлов
  const traverseFileTree = (item, fileList) => {
    if (item.isFile) {
      item.file((file) => {
        fileList.push(file);
      });
    } else if (item.isDirectory) {
      const dirReader = item.createReader();
      dirReader.readEntries((entries) => {
        for (let i = 0; i < entries.length; i++) {
          traverseFileTree(entries[i], fileList);
        }
      });
    }
  };

  const handleProcess = () => {
    if (selectedFiles.length === 0) {
      alert('Пожалуйста, выберите файлы для обработки');
      return;
    }
    onProcess(selectedFiles, fileType, threshold);
  };

  const clearSelection = () => {
    setSelectedFiles([]);
    setFolderName('');
    setFileType('nii');
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (folderInputRef.current) folderInputRef.current.value = '';
    if (stlInputRef.current) stlInputRef.current.value = '';
  };

  const getTotalSize = () => {
    const totalBytes = selectedFiles.reduce((sum, file) => sum + file.size, 0);
    return (totalBytes / 1024 / 1024).toFixed(2);
  };

  return (
    <div className="file-uploader">
      <h2>Загрузка медицинских изображений</h2>
     
      <div className="upload-options">
        {/* <div className="upload-section">
          <h3>NIfTI файлы (.nii, .nii.gz)</h3>
          <div
            className={`drop-zone ${dragOver && fileType === 'nii' ? 'drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, false)}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".nii,.nii.gz"
              multiple
              onChange={(e) => handleFileSelect(e, false)}
              style={{ display: 'none' }}
            />
            <div className="drop-zone-content">
              <div className="upload-icon">📄</div>
              <p>Перетащите NIfTI файлы сюда или кликните для выбора</p>
              <small>Поддерживаются: .nii, .nii.gz</small>
            </div>
          </div>
        </div> */}

        <div className="upload-section">
          <h3>DICOM папка</h3>
          <div
            className={`drop-zone ${dragOver && (fileType === 'dicom' || fileType === 'dicom_zip') ? 'drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, true)}
            onClick={() => folderInputRef.current?.click()}
          >
            <input
              ref={folderInputRef}
              type="file"
              webkitdirectory="true"
              directory="true"
              multiple
              onChange={(e) => handleFileSelect(e, true)}
              style={{ display: 'none' }}
            />
            <div className="drop-zone-content">
              <div className="upload-icon">📁</div>
              <p>Нажмите сюда чтобы загрузить папку с DICOM файлами</p>
              </div>
          </div>
        </div>

        <div className="upload-section">
          <h3>STL файлы (3D модели)</h3>
          <div
            className={`drop-zone ${dragOver && fileType === 'stl' ? 'drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, false, true)}
            onClick={() => stlInputRef.current?.click()}
          >
            <input
              ref={stlInputRef}
              type="file"
              accept=".stl"
              multiple
              onChange={(e) => handleFileSelect(e, false, true)}
              style={{ display: 'none' }}
            />
            <div className="drop-zone-content">
              <div className="upload-icon">🔷</div>
              <p>Перетащите STL файлы сюда или кликните для выбора</p>
              <small>Для просмотра готовых 3D моделей (можно выбрать несколько)</small>
            </div>
          </div>
        </div>
      </div>

      {selectedFiles.length > 0 && (
        <div className="selected-files">
          <h4>Выбранные файлы:</h4>
          
          {folderName && (
            <div className="folder-info">
              <strong>Папка:</strong> {folderName}
            </div>
          )}
          
          <div className="files-summary">
            <span>Файлов: {selectedFiles.length}</span>
            <span>Общий размер: {getTotalSize()} MB</span>
          </div>

          <div className="files-list">
            {selectedFiles.slice(0, 10).map((file, index) => (
              <div key={index} className="file-item">
                <span className="file-name">{file.name}</span>
                <span className="file-size">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
              </div>
            ))}
            {selectedFiles.length > 10 && (
              <div className="more-files">
                + еще {selectedFiles.length - 10} файлов...
              </div>
            )}
          </div>
          
          {fileType === 'nii' && (
            <div className="threshold-control">
              <label>
                Порог сегментации:
                <input
                  type="range"
                  min="0.1"
                  max="0.9"
                  step="0.1"
                  value={threshold}
                  onChange={(e) => setThreshold(parseFloat(e.target.value))}
                />
                <span>{threshold}</span>
              </label>
            </div>
          )}

          <div className="action-buttons">
            <button onClick={clearSelection} disabled={isLoading}>
              Очистить
            </button>
            {fileType !== 'stl' && (
              <button 
                onClick={handleProcess} 
                disabled={isLoading}
                className="process-btn"
              >
                {isLoading ? 'Обработка...' : `Запустить обработку (${selectedFiles.length} файлов)`}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUploader;